import { defineComponent as _defineComponent } from 'vue'
import { createVNode as _createVNode, createElementVNode as _createElementVNode, renderList as _renderList, Fragment as _Fragment, openBlock as _openBlock, createElementBlock as _createElementBlock, createCommentVNode as _createCommentVNode, resolveComponent as _resolveComponent, createBlock as _createBlock, withCtx as _withCtx, toDisplayString as _toDisplayString, normalizeClass as _normalizeClass, unref as _unref, createTextVNode as _createTextVNode, resolveDirective as _resolveDirective, withDirectives as _withDirectives } from "vue"

const _hoisted_1 = { class: "trigger-box-wrapper" }
const _hoisted_2 = { class: "trigger-box-section" }
const _hoisted_3 = { class: "trigger-options" }
const _hoisted_4 = ["onClick", "id"]
const _hoisted_5 = { class: "icon is-medium trigger-option-icon" }
const _hoisted_6 = {
  key: 0,
  class: "custom-icon"
}
const _hoisted_7 = ["xlink:href"]
const _hoisted_8 = { class: "has-text-centered is-size-7 trigger-option-text" }
const _hoisted_9 = {
  key: 0,
  class: "help is-danger"
}
const _hoisted_10 = { class: "trigger-box-section" }
const _hoisted_11 = { class: "trigger-box-section" }
const _hoisted_12 = { class: "subtitle is-6" }
const _hoisted_13 = {
  key: 0,
  class: "invalid-warning"
}

import { AvailableTriggers, TriggerType } from '@/features/bots/models/designer';
import {
  SET_TRIGGER,
  UPDATE_SCHEDULE_CONDITION,
  UPDATE_TRIGGER_CONDITIONS,
  UPDATE_AUDIENCE_CONDITIONS,
  UPDATE_TARGET_CONTACT_TYPES,
} from '@/store/bot-designer/triggers/mutationTypes';
import TriggerScheduler from './TriggerScheduler.vue';
import AdditionalInformationWrapper from './AdditionalInformationWrapper.vue';
import ErrorIcon from 'vue-material-design-icons/AlertCircleOutline.vue';
import Triggers from '@/features/bots/components/designer/Triggers.vue';
import { BusinessHoursViewModel } from '../../models';
import { TriggerValidity } from '@/features/bots/models/designer/triggerValidity';
import { NLP_ENABLED } from '@/store/app/featureTypes';
import { ConditionGroupViewModel } from '@/shared/components/conditions/models/conditionGroupViewModel';
import { ConditionViewModel } from '@/shared/components/conditions/models/conditionViewModel';
import { ConditionGroup } from '@/shared/components/conditions/models/conditionGroup';
import ConditionGroupBuilder, { ConditionGroupUpdate } from '@/shared/components/conditions/ConditionGroupBuilder.vue';
import { ContactType } from '@/features/contacts/models/contactType';
import MultiContactTypePicker from '@/features/bots/components/designer/MultiContactTypePicker.vue';
import { ChannelIdentityViewModel } from '@/messaging/models/channelIdentityViewModel';
import { PROACTIVE_FLOWS_ENABLED } from '@/store/app/featureTypes';
import { computed, nextTick, onMounted, onUnmounted, PropType, ref, watch } from 'vue';
import { useStore } from 'vuex';
import {
  TriggerConditionAttributeType,
  useTriggerConditions,
} from '@/features/bots/components/designer/Conditions/useTriggerConditions';
import { useAudienceConditions } from '@/features/bots/components/designer/Conditions/useAudienceConditions';


export default /*@__PURE__*/_defineComponent({
  __name: 'TriggerBox',
  props: {
  value: {
    type: String,
    required: true,
  },
  triggerConditions: {
    type: Object as PropType<ConditionGroupViewModel>,
    required: true,
  },
  audienceConditions: {
    type: Object as PropType<ConditionGroupViewModel>,
    required: true,
  },
  schedule: {
    type: Object as PropType<ConditionViewModel>,
  },
  openingTimes: {
    type: Array as PropType<BusinessHoursViewModel[]>,
    default: [],
  },
},
  setup(__props) {

const props = __props;

const store = useStore();
const triggerValidity = computed<TriggerValidity>(() => store.getters.triggerValidity);
const isWorkspaceFeatureEnabled = (feature: string, defaultValue = false) =>
  computed(() => store.getters.isWorkspaceFeatureEnabled(feature, defaultValue));
const channelIdentities = computed<ChannelIdentityViewModel[]>(() => store.getters.channelIdentities);
const contactTypes = computed(() => store.getters.targetContactTypes);

const setTrigger = (trigger: string) => store.commit(SET_TRIGGER, trigger);

const updateTriggerConditions = (conditions: ConditionGroupViewModel) =>
  store.commit(UPDATE_TRIGGER_CONDITIONS, conditions);
const updateAudienceConditions = (conditions: ConditionGroupViewModel) =>
  store.commit(UPDATE_AUDIENCE_CONDITIONS, conditions);
const updateScheduleCondition = (condition: ConditionViewModel, openingTimes: BusinessHoursViewModel[]) =>
  store.commit(UPDATE_SCHEDULE_CONDITION, { condition, openingTimes });
const updateContactTypes = (contactTypes: string[]) => store.commit(UPDATE_TARGET_CONTACT_TYPES, contactTypes);

const triggerType = ref('');
const scheduleCondition = ref<ConditionViewModel>({} as ConditionViewModel);
const showConfig = ref(false);
const excludedTriggerTypes = ref<string[]>([]);
const triggerConditionList = ref<ConditionGroupViewModel>({
  type: 'and',
  conditions: [],
});
const audienceConditionList = ref<ConditionGroupViewModel>({
  type: 'and',
  conditions: [],
});

const triggerConditionsManager = useTriggerConditions();
const audienceConditionsManager = useAudienceConditions();

const disableClickAway = ref(false);
const conditionConfigOpen = ref(false);
const isAddNewPickerOpen = ref(false);
const availableTypes = AvailableTriggers.list();
const availableTriggerTypes = computed(() =>
  availableTypes.filter((trigger) => !excludedTriggerTypes.value.includes(trigger.type)),
);

function setConditionOpenedState(isOpen: boolean) {
  conditionConfigOpen.value = isOpen;
}

watch(
  () => props.value,
  (value: string) => (triggerType.value = value),
  { immediate: true },
);
watch(
  () => props.triggerConditions,
  (value: ConditionGroupViewModel) => (triggerConditionList.value = value),
  { immediate: true },
);
watch(
  () => props.audienceConditions,
  (value: ConditionGroupViewModel) => (audienceConditionList.value = value),
  { immediate: true },
);
watch(
  () => props.schedule,
  (value: ConditionViewModel) => (scheduleCondition.value = value),
  { immediate: true },
);

onMounted(() => {
  getExcludedTriggerTypes();
  triggerConditionsManager.excludeConditions(getExcludedConditions());
});

function triggerConditionsUpdated(update: ConditionGroupUpdate) {
  triggerConditionsManager.excludeConditions(getExcludedConditions());
  updateTriggerConditions(update.conditionGroup);
  keepTriggerConfigOpenOnUpdate();
}

function audienceConditionsUpdated(update: ConditionGroupUpdate) {
  triggerConditionsManager.excludeConditions(getExcludedConditions());
  updateAudienceConditions(update.conditionGroup);
  keepTriggerConfigOpenOnUpdate();
}

function contactTypeUpdated(newContactTypes: ContactType[]) {
  updateContactTypes(newContactTypes);
}

function keepTriggerConfigOpenOnUpdate() {
  showTriggerConfig();
  disableClickAway.value = true;
  setTimeout(() => (disableClickAway.value = false), 500); // Hack, clickaway kicks in when some elements selected in conditin pickers
}

function showTriggerConfig() {
  showConfig.value = true;
}

function hideTriggerConfig() {
  if (conditionConfigOpen.value || disableClickAway.value) return;
  showConfig.value = false;
  isAddNewPickerOpen.value = false;
}

function isTriggerSelected(type: TriggerType) {
  return triggerType.value === type;
}

function selectTriggerType(type: TriggerType) {
  handleSwitchingTrigger(type);
  if (triggerType.value === type) {
    triggerType.value = '';
  } else {
    triggerType.value = type;
  }
  setTrigger(triggerType.value);
  triggerConditionsManager.excludeConditions(getExcludedConditions());
}

function onScheduleUpdated(condition: ConditionViewModel | undefined, customTimes: BusinessHoursViewModel[]) {
  updateScheduleCondition(condition!, customTimes);
}

async function getExcludedTriggerTypes() {
  if (!isProactiveFlowsEnabled()) {
    excludedTriggerTypes.value.push(TriggerType.WidgetLoaded);
  }
}

function isProactiveFlowsEnabled() {
  return isWorkspaceFeatureEnabled(PROACTIVE_FLOWS_ENABLED).value;
}

function getExcludedConditions() {
  const excludedConditions: string[] = [];
  if (!isTriggerSelected(TriggerType.MessageReceived)) {
    excludedConditions.push(TriggerConditionAttributeType.MessageText);
    excludedConditions.push(TriggerConditionAttributeType.MessageIntent);
  } else if (!isWorkspaceFeatureEnabled(NLP_ENABLED).value) {
    excludedConditions.push(TriggerConditionAttributeType.MessageIntent);
  } else {
    if (triggerConditionList.value) {
      const messageCondition = ConditionGroup.find(
        triggerConditionList.value,
        (c) => c.attribute === TriggerConditionAttributeType.MessageText,
      );
      if (messageCondition) {
        excludedConditions.push(TriggerConditionAttributeType.MessageIntent);
      }

      const intentCondition = ConditionGroup.find(
        triggerConditionList.value,
        (c) => c.attribute === TriggerConditionAttributeType.MessageIntent,
      );
      if (intentCondition) {
        excludedConditions.push(TriggerConditionAttributeType.MessageText);
        excludedConditions.push(TriggerConditionAttributeType.MessageIntent);
      }
    }
  }
  if (
    isTriggerSelected(TriggerType.WidgetLoaded) ||
    isTriggerSelected(TriggerType.ConversationOpened) ||
    channelIdentities.value.length === 0 ||
    !isWorkspaceFeatureEnabled('multipleIdentitiesPerChannelEnabled')
  ) {
    excludedConditions.push(TriggerConditionAttributeType.ChannelIdentity);
  }

  if (!isTriggerSelected(TriggerType.WidgetLoaded)) {
    excludedConditions.push(TriggerConditionAttributeType.TimeOnPage);
  } else {
    if (triggerConditionList.value) {
      const timeOnPageCondition = ConditionGroup.find(
        triggerConditionList.value,
        (c) => c.attribute === TriggerConditionAttributeType.TimeOnPage,
      );
      if (timeOnPageCondition) {
        excludedConditions.push(TriggerConditionAttributeType.TimeOnPage);
      }
    }
  }
  return excludedConditions;
}

function getTooltipForTrigger(type: TriggerType) {
  if (type === TriggerType.MessageReceived) {
    return `A message is received and the user's intention is recognised`;
  } else if (type === TriggerType.Unknown) {
    return `A message is received and the user's intention is not recognised`;
  } else if (type === TriggerType.ConversationOpened) {
    return `When the user opens the Web Assistant conversation`;
  } else if (type === TriggerType.WidgetLoaded) {
    return `User opens a webpage with Web Assistant installed`;
  }
}

function handleSwitchingTrigger(type: string) {
  const invalidAttributes: string[] = [];
  if (type !== TriggerType.MessageReceived) {
    invalidAttributes.push(TriggerConditionAttributeType.MessageText);
    invalidAttributes.push(TriggerConditionAttributeType.MessageIntent);
  }

  if (type !== TriggerType.WidgetLoaded) {
    invalidAttributes.push(TriggerConditionAttributeType.TimeOnPage);
  }

  if (type !== TriggerType.MessageReceived && type !== TriggerType.Unknown) {
    invalidAttributes.push(TriggerConditionAttributeType.ChannelIdentity);
  }

  if (invalidAttributes.length > 0) {
    triggerConditionList.value = removeInvalidConditions(triggerConditionList.value, invalidAttributes);
    updateTriggerConditions(triggerConditionList.value);
  }
}

function removeInvalidConditions(
  conditionGroup: ConditionGroupViewModel,
  invalidAttributes: string[],
): ConditionGroupViewModel {
  for (let i = conditionGroup.conditions.length - 1; i >= 0; i--) {
    const c = conditionGroup.conditions[i];
    if (ConditionGroup.isCondition(c)) {
      if (invalidAttributes.includes(c.attribute)) {
        conditionGroup.conditions.splice(i, 1);
      }
    } else {
      removeInvalidConditions(c, invalidAttributes);
    }
  }

  return conditionGroup;
}

return (_ctx: any,_cache: any) => {
  const _component_font_awesome_icon = _resolveComponent("font-awesome-icon")!
  const _component_n_tooltip = _resolveComponent("n-tooltip")!
  const _directive_click_away = _resolveDirective("click-away")!

  return (_openBlock(), _createElementBlock("div", _hoisted_1, [
    _withDirectives((_openBlock(), _createElementBlock("div", {
      class: "trigger-config",
      onClick: _cache[4] || (_cache[4] = ($event: any) => (showTriggerConfig()))
    }, [
      _createVNode(Triggers, { conditions: __props.triggerConditions }, null, 8, ["conditions"]),
      (showConfig.value)
        ? (_openBlock(), _createBlock(AdditionalInformationWrapper, {
            key: 0,
            color: "#E6C300",
            "text-color": "#212529"
          }, {
            content: _withCtx(() => [
              _createElementVNode("div", _hoisted_2, [
                _cache[5] || (_cache[5] = _createElementVNode("h6", { class: "title is-6" }, [
                  _createElementVNode("strong", null, "Trigger")
                ], -1)),
                _cache[6] || (_cache[6] = _createElementVNode("h6", { class: "subtitle is-6" }, "Choose an action that will trigger the flow", -1)),
                _createElementVNode("div", _hoisted_3, [
                  (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(availableTriggerTypes.value, (option) => {
                    return (_openBlock(), _createElementBlock("div", {
                      class: _normalizeClass(["trigger-option", { selected: isTriggerSelected(option.type) }]),
                      key: option.type,
                      onClick: ($event: any) => (selectTriggerType(option.type)),
                      id: option.type === 'unknown' ? 'unknown-intent-trigger' : undefined
                    }, [
                      _createVNode(_component_n_tooltip, {
                        position: "bottom",
                        text: getTooltipForTrigger(option.type)
                      }, {
                        default: _withCtx(() => [
                          _createElementVNode("span", _hoisted_5, [
                            (option.icon.startsWith('#'))
                              ? (_openBlock(), _createElementBlock("svg", _hoisted_6, [
                                  _createElementVNode("use", {
                                    "xmlns:xlink": "http://www.w3.org/1999/xlink",
                                    "xlink:href": `${option.icon}`
                                  }, null, 8, _hoisted_7)
                                ]))
                              : _createCommentVNode("", true),
                            (!option.icon.startsWith('#'))
                              ? (_openBlock(), _createBlock(_component_font_awesome_icon, {
                                  key: 1,
                                  icon: ['far', option.icon],
                                  size: "2x"
                                }, null, 8, ["icon"]))
                              : _createCommentVNode("", true)
                          ])
                        ]),
                        _: 2
                      }, 1032, ["text"]),
                      _createElementVNode("div", _hoisted_8, _toDisplayString(option.description), 1)
                    ], 10, _hoisted_4))
                  }), 128))
                ]),
                (!triggerValidity.value.isValid)
                  ? (_openBlock(), _createElementBlock("p", _hoisted_9, _toDisplayString(triggerValidity.value.message), 1))
                  : _createCommentVNode("", true),
                _cache[7] || (_cache[7] = _createElementVNode("h6", { class: "subtitle is-6" }, "When these conditions are met", -1)),
                _createVNode(ConditionGroupBuilder, {
                  value: triggerConditionList.value,
                  manager: _unref(triggerConditionsManager),
                  context: "condition",
                  onUpdated: triggerConditionsUpdated,
                  onConfigOpened: _cache[0] || (_cache[0] = ($event: any) => (setConditionOpenedState(true))),
                  onConfigClosed: _cache[1] || (_cache[1] = ($event: any) => (setConditionOpenedState(false)))
                }, null, 8, ["value", "manager"])
              ]),
              _createElementVNode("div", _hoisted_10, [
                _cache[8] || (_cache[8] = _createElementVNode("h6", { class: "title is-6" }, [
                  _createElementVNode("strong", null, "Audience")
                ], -1)),
                _cache[9] || (_cache[9] = _createElementVNode("h6", { class: "subtitle is-6" }, "Select which contacts are sent the flow", -1)),
                _createVNode(ConditionGroupBuilder, {
                  value: audienceConditionList.value,
                  manager: _unref(audienceConditionsManager),
                  context: "condition",
                  onUpdated: audienceConditionsUpdated,
                  onConfigOpened: _cache[2] || (_cache[2] = ($event: any) => (setConditionOpenedState(true))),
                  onConfigClosed: _cache[3] || (_cache[3] = ($event: any) => (setConditionOpenedState(false)))
                }, {
                  before: _withCtx(() => [
                    _createVNode(MultiContactTypePicker, {
                      class: "contact-type-picker",
                      "model-value": contactTypes.value,
                      onUpdatedType: contactTypeUpdated
                    }, null, 8, ["model-value"])
                  ]),
                  _: 1
                }, 8, ["value", "manager"])
              ]),
              _createElementVNode("div", _hoisted_11, [
                _cache[11] || (_cache[11] = _createElementVNode("h6", { class: "title is-6" }, [
                  _createElementVNode("strong", null, "Schedule")
                ], -1)),
                _createElementVNode("h6", _hoisted_12, [
                  _cache[10] || (_cache[10] = _createTextVNode(" Decide when the flow should interact with your visitors ", -1)),
                  _createVNode(_component_n_tooltip, {
                    position: "top",
                    text: "Your default business hours are defined in workspace settings"
                  })
                ]),
                _createVNode(TriggerScheduler, {
                  condition: scheduleCondition.value,
                  openingTimes: __props.openingTimes,
                  onUpdated: onScheduleUpdated
                }, null, 8, ["condition", "openingTimes"])
              ])
            ]),
            _: 1
          }))
        : _createCommentVNode("", true)
    ])), [
      [_directive_click_away, hideTriggerConfig]
    ]),
    (!triggerValidity.value.isValid)
      ? (_openBlock(), _createElementBlock("div", _hoisted_13, [
          _createVNode(ErrorIcon, { class: "icon-1-3x" })
        ]))
      : _createCommentVNode("", true)
  ]))
}
}

})